import { useNavigate } from 'react-router-dom';
import styles from './CreateAccountPage.module.css';
import { openLiveChat } from '@/components/common/LiveChatWidget';

// ---------------------------------------------------------------------------
// MangoGrid power-grid icon — hexagonal with internal lines (amber brand mark)
// ---------------------------------------------------------------------------

function MangoGridIcon({ size = 36 }: { size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Outer hexagon */}
      <polygon
        points="24,4 42,14 42,34 24,44 6,34 6,14"
        fill="none"
        stroke="#f59e0b"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      {/* Internal grid lines */}
      <line x1="24" y1="4" x2="24" y2="44" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <line x1="6" y1="14" x2="42" y2="34" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <line x1="42" y1="14" x2="6" y2="34" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      {/* Power bolt */}
      <polygon
        points="26,16 20,26 24,26 22,34 30,22 26,22"
        fill="#f59e0b"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// GridArtSVG — abstract transmission network background art
// ---------------------------------------------------------------------------

function GridArtSVG(): JSX.Element {
  return (
    <svg
      className={styles.heroBackground}
      viewBox="0 0 1200 420"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="caSkyGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#9abdd4" />
          <stop offset="50%"  stopColor="#c8a050" />
          <stop offset="100%" stopColor="#8c6020" />
        </linearGradient>
        <linearGradient id="caBarGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#34d399" />
          <stop offset="100%" stopColor="#0891b2" />
        </linearGradient>
      </defs>

      {/* Warm sky background */}
      <rect width="1200" height="420" fill="url(#caSkyGrad)" />

      {/* Graph paper grid */}
      <g stroke="rgba(70,110,160,0.16)" strokeWidth="0.9">
        <line x1="0"    y1="50"  x2="1200" y2="50"  />
        <line x1="0"    y1="100" x2="1200" y2="100" />
        <line x1="0"    y1="150" x2="1200" y2="150" />
        <line x1="0"    y1="200" x2="1200" y2="200" />
        <line x1="0"    y1="250" x2="1200" y2="250" />
        <line x1="0"    y1="300" x2="1200" y2="300" />
        <line x1="0"    y1="350" x2="1200" y2="350" />
        <line x1="0"    y1="400" x2="1200" y2="400" />
        <line x1="50"   y1="0"   x2="50"   y2="420" />
        <line x1="100"  y1="0"   x2="100"  y2="420" />
        <line x1="150"  y1="0"   x2="150"  y2="420" />
        <line x1="200"  y1="0"   x2="200"  y2="420" />
        <line x1="250"  y1="0"   x2="250"  y2="420" />
        <line x1="300"  y1="0"   x2="300"  y2="420" />
        <line x1="350"  y1="0"   x2="350"  y2="420" />
        <line x1="400"  y1="0"   x2="400"  y2="420" />
        <line x1="450"  y1="0"   x2="450"  y2="420" />
        <line x1="500"  y1="0"   x2="500"  y2="420" />
        <line x1="550"  y1="0"   x2="550"  y2="420" />
        <line x1="600"  y1="0"   x2="600"  y2="420" />
        <line x1="650"  y1="0"   x2="650"  y2="420" />
        <line x1="700"  y1="0"   x2="700"  y2="420" />
        <line x1="750"  y1="0"   x2="750"  y2="420" />
        <line x1="800"  y1="0"   x2="800"  y2="420" />
        <line x1="850"  y1="0"   x2="850"  y2="420" />
        <line x1="900"  y1="0"   x2="900"  y2="420" />
        <line x1="950"  y1="0"   x2="950"  y2="420" />
        <line x1="1000" y1="0"   x2="1000" y2="420" />
        <line x1="1050" y1="0"   x2="1050" y2="420" />
        <line x1="1100" y1="0"   x2="1100" y2="420" />
        <line x1="1150" y1="0"   x2="1150" y2="420" />
      </g>

      {/* Cooling tower silhouettes */}
      <path d="M55,420 L90,248 Q112,208 136,248 L172,420 Z"  fill="rgba(55,42,22,0.28)" />
      <path d="M155,420 L198,225 Q222,180 248,225 L292,420 Z" fill="rgba(55,42,22,0.26)" />
      <rect x="308" y="288" width="16" height="132" rx="2"    fill="rgba(50,38,18,0.25)" />
      <circle cx="316" cy="274" r="14" fill="rgba(210,200,180,0.11)" />
      <circle cx="314" cy="250" r="20" fill="rgba(210,200,180,0.07)" />
      {/* House + solar */}
      <rect x="355" y="358" width="78" height="62" rx="2"     fill="rgba(50,44,28,0.26)" />
      <polygon points="350,358 438,358 395,320"                fill="rgba(42,36,20,0.28)" />
      <rect x="364" y="326" width="64" height="30" rx="2"
            fill="rgba(14,100,210,0.38)" stroke="rgba(14,165,233,0.48)" strokeWidth="1" />
      <line x1="379" y1="327" x2="377" y2="354" stroke="rgba(14,165,233,0.36)" strokeWidth="0.8" />
      <line x1="397" y1="326" x2="395" y2="353" stroke="rgba(14,165,233,0.36)" strokeWidth="0.8" />
      <line x1="415" y1="326" x2="413" y2="353" stroke="rgba(14,165,233,0.36)" strokeWidth="0.8" />
      <line x1="366" y1="337" x2="427" y2="335" stroke="rgba(14,165,233,0.36)" strokeWidth="0.8" />
      <line x1="365" y1="347" x2="426" y2="345" stroke="rgba(14,165,233,0.36)" strokeWidth="0.8" />
      <rect x="443" y="370" width="56" height="50" rx="2"     fill="rgba(50,44,28,0.20)" />
      <polygon points="438,370 504,370 473,342"                fill="rgba(42,36,20,0.22)" />

      {/* Bar chart — right portion */}
      <rect x="720"  y="308" width="22" height="117" rx="2" fill="url(#caBarGrad)" fillOpacity="0.70" />
      <rect x="750"  y="272" width="22" height="153" rx="2" fill="url(#caBarGrad)" fillOpacity="0.72" />
      <rect x="780"  y="322" width="22" height="103" rx="2" fill="url(#caBarGrad)" fillOpacity="0.68" />
      <rect x="810"  y="248" width="22" height="177" rx="2" fill="url(#caBarGrad)" fillOpacity="0.75" />
      <rect x="840"  y="288" width="22" height="137" rx="2" fill="url(#caBarGrad)" fillOpacity="0.70" />
      <rect x="870"  y="260" width="22" height="165" rx="2" fill="url(#caBarGrad)" fillOpacity="0.72" />
      <rect x="900"  y="297" width="22" height="128" rx="2" fill="url(#caBarGrad)" fillOpacity="0.68" />
      <rect x="930"  y="233" width="22" height="192" rx="2" fill="url(#caBarGrad)" fillOpacity="0.78" />
      <rect x="960"  y="268" width="22" height="157" rx="2" fill="url(#caBarGrad)" fillOpacity="0.70" />
      <rect x="990"  y="250" width="22" height="175" rx="2" fill="url(#caBarGrad)" fillOpacity="0.72" />
      <rect x="1020" y="282" width="22" height="143" rx="2" fill="url(#caBarGrad)" fillOpacity="0.68" />
      <rect x="1050" y="213" width="22" height="212" rx="2" fill="url(#caBarGrad)" fillOpacity="0.78" />
      <rect x="1080" y="257" width="22" height="168" rx="2" fill="url(#caBarGrad)" fillOpacity="0.72" />
      <rect x="1110" y="237" width="22" height="188" rx="2" fill="url(#caBarGrad)" fillOpacity="0.75" />
      <rect x="1140" y="222" width="22" height="203" rx="2" fill="url(#caBarGrad)" fillOpacity="0.78" />

      {/* Teal line chart */}
      <polyline
        points="20,322 150,300 280,274 420,270 560,252 700,240 850,220 1000,200 1150,180"
        stroke="#06b6d4" strokeWidth="3" fill="none"
        strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.90"
      />
      {/* Yellow line chart */}
      <polyline
        points="20,358 150,330 280,302 420,287 560,262 700,232 850,200 1000,167 1150,140"
        stroke="#facc15" strokeWidth="3.5" fill="none"
        strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.95"
      />

      {/* Data points */}
      <circle cx="150"  cy="300" r="6" fill="#06b6d4" stroke="#cffafe" strokeWidth="2" />
      <circle cx="420"  cy="270" r="6" fill="#06b6d4" stroke="#cffafe" strokeWidth="2" />
      <circle cx="700"  cy="240" r="6" fill="#06b6d4" stroke="#cffafe" strokeWidth="2" />
      <circle cx="1000" cy="200" r="6" fill="#06b6d4" stroke="#cffafe" strokeWidth="2" />
      <circle cx="150"  cy="330" r="6" fill="#facc15" stroke="#fef9c3" strokeWidth="2" />
      <circle cx="420"  cy="287" r="7" fill="#facc15" stroke="#fef9c3" strokeWidth="2" />
      <circle cx="700"  cy="232" r="6" fill="#facc15" stroke="#fef9c3" strokeWidth="2" />
      <circle cx="1000" cy="167" r="7" fill="#facc15" stroke="#fef9c3" strokeWidth="2" />
      <circle cx="560"  cy="252" r="7" fill="#f97316" stroke="#fed7aa" strokeWidth="2" />
      <circle cx="560"  cy="262" r="8" fill="#34d399" stroke="#d1fae5" strokeWidth="2.5" />
      <circle cx="850"  cy="200" r="7" fill="#a78bfa" stroke="#ede9fe" strokeWidth="2" />

    </svg>
  );
}

// ---------------------------------------------------------------------------
// Persona tile icons — simple geometric inline SVGs
// ---------------------------------------------------------------------------

function IconGridOperator(): JSX.Element {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Grid bars + lightning bolt — represents control room operator */}
      <rect x="2" y="3" width="20" height="14" rx="2" stroke="#0ea5e9" strokeWidth="1.5" fill="none" />
      <line x1="2" y1="9" x2="22" y2="9" stroke="#0ea5e9" strokeWidth="1" strokeOpacity="0.5" />
      <line x1="8" y1="3" x2="8" y2="17" stroke="#0ea5e9" strokeWidth="1" strokeOpacity="0.5" />
      <line x1="16" y1="3" x2="16" y2="17" stroke="#0ea5e9" strokeWidth="1" strokeOpacity="0.5" />
      <polygon points="13,5.5 10,10.5 12,10.5 11,14.5 15,8.5 13,8.5" fill="#f59e0b" />
      <line x1="8" y1="20" x2="16" y2="20" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12" y1="17" x2="12" y2="20" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconMarketAnalyst(): JSX.Element {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Bar chart + trend line — LMP price chart */}
      <rect x="3" y="13" width="3" height="7" rx="1" fill="#0ea5e9" fillOpacity="0.7" />
      <rect x="8" y="9" width="3" height="11" rx="1" fill="#0ea5e9" fillOpacity="0.85" />
      <rect x="13" y="5" width="3" height="15" rx="1" fill="#0ea5e9" />
      <rect x="18" y="10" width="3" height="10" rx="1" fill="#0ea5e9" fillOpacity="0.75" />
      {/* Trend line overlay */}
      <polyline points="4.5,12 9.5,8 14.5,4 19.5,9" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="4.5" cy="12" r="1.5" fill="#f59e0b" />
      <circle cx="14.5" cy="4" r="1.5" fill="#f59e0b" />
    </svg>
  );
}

function IconResearcher(): JSX.Element {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Network/circuit node diagram — IEEE bus network */}
      <circle cx="12" cy="12" r="2.5" fill="#0ea5e9" />
      <circle cx="5" cy="7" r="2" stroke="#0ea5e9" strokeWidth="1.5" fill="none" />
      <circle cx="19" cy="7" r="2" stroke="#0ea5e9" strokeWidth="1.5" fill="none" />
      <circle cx="5" cy="17" r="2" stroke="#0ea5e9" strokeWidth="1.5" fill="none" />
      <circle cx="19" cy="17" r="2" stroke="#0ea5e9" strokeWidth="1.5" fill="none" />
      <line x1="6.8" y1="8.2" x2="10.3" y2="10.7" stroke="#64748b" strokeWidth="1" />
      <line x1="17.2" y1="8.2" x2="13.7" y2="10.7" stroke="#64748b" strokeWidth="1" />
      <line x1="6.8" y1="15.8" x2="10.3" y2="13.3" stroke="#64748b" strokeWidth="1" />
      <line x1="17.2" y1="15.8" x2="13.7" y2="13.3" stroke="#64748b" strokeWidth="1" />
      <line x1="5" y1="9" x2="5" y2="15" stroke="#f59e0b" strokeWidth="1" strokeOpacity="0.6" />
      <line x1="19" y1="9" x2="19" y2="15" stroke="#f59e0b" strokeWidth="1" strokeOpacity="0.6" />
    </svg>
  );
}

function IconUtility(): JSX.Element {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Building / institution — utility company */}
      <rect x="3" y="10" width="18" height="11" rx="1" stroke="#0ea5e9" strokeWidth="1.5" fill="none" />
      <line x1="12" y1="4" x2="3" y2="10" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12" y1="4" x2="21" y2="10" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="7" y="14" width="3" height="7" rx="0.5" fill="#0ea5e9" fillOpacity="0.5" />
      <rect x="14" y="14" width="3" height="7" rx="0.5" fill="#0ea5e9" fillOpacity="0.5" />
      {/* Antenna / transmission tower on roof */}
      <line x1="12" y1="4" x2="12" y2="1" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="10" y1="2.5" x2="14" y2="2.5" stroke="#f59e0b" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Feature item helpers
// ---------------------------------------------------------------------------

interface FeatureItemProps {
  included: boolean;
  text: string;
}

function FeatureItem({ included, text }: FeatureItemProps): JSX.Element {
  return (
    <li className={styles.featureItem}>
      <span
        className={included ? styles.featureCheck : styles.featureCross}
        aria-hidden="true"
      >
        {included ? '✓' : '✗'}
      </span>
      <span style={{ color: included ? '#374151' : '#9ca3af' }}>{text}</span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Feature comparison table data — power-systems specific
// ---------------------------------------------------------------------------

interface ComparisonRow {
  feature: string;
  free: 'yes' | 'no' | string;
  trial: 'yes' | 'no' | string;
  enterprise: 'yes' | 'no' | string;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: 'IEEE 118-bus network viewer', free: 'yes', trial: 'yes', enterprise: 'yes' },
  { feature: 'SCUC solver (HiGHS)', free: 'yes', trial: 'yes', enterprise: 'yes' },
  { feature: 'Saved scenarios', free: '5', trial: '50', enterprise: 'Unlimited' },
  { feature: 'IEEE / RTS-GMLC test library', free: 'yes', trial: 'yes', enterprise: 'yes' },
  { feature: 'N-1 contingency viewer', free: 'yes', trial: 'yes', enterprise: 'yes' },
  { feature: 'Real-time ISO LMP data (PJM/MISO/CAISO)', free: 'no', trial: 'yes', enterprise: 'yes' },
  { feature: 'Benders decomposition solver', free: 'no', trial: 'yes', enterprise: 'yes' },
  { feature: 'ADMM zonal coordination', free: 'no', trial: 'yes', enterprise: 'yes' },
  { feature: 'PTDF/LODF matrix computation', free: 'no', trial: 'yes', enterprise: 'yes' },
  { feature: 'FTR opportunity analysis', free: 'no', trial: 'yes', enterprise: 'yes' },
  { feature: 'AI Analyst (natural language)', free: 'no', trial: 'yes', enterprise: 'yes' },
  { feature: 'Stochastic UC (Monte Carlo)', free: 'no', trial: 'no', enterprise: 'yes' },
  { feature: 'Multi-user workspace', free: 'no', trial: 'no', enterprise: 'yes' },
  { feature: 'On-premise deployment', free: 'no', trial: 'no', enterprise: 'yes' },
];

function CellValue({ value }: { value: string }): JSX.Element {
  if (value === 'yes') {
    return <span className={styles.tableCheck} aria-label="Included">✓</span>;
  }
  if (value === 'no') {
    return <span className={styles.tableCross} aria-label="Not included">—</span>;
  }
  return <span className={styles.tablePartial}>{value}</span>;
}

// ---------------------------------------------------------------------------
// Solver & Standards Stack data
// ---------------------------------------------------------------------------

interface SolverBadgeData {
  name: string;
  desc: string;
}

const SOLVER_BADGES: SolverBadgeData[] = [
  { name: 'HiGHS', desc: 'Open-source MILP/LP solver' },
  { name: 'Benders', desc: 'Decomposition for large-scale UC' },
  { name: 'ADMM', desc: 'Zonal coordination at ISO scale' },
  { name: 'PJM Manual 11', desc: 'LMP decomposition standard' },
  { name: 'IEEE 118-Bus', desc: 'Canonical test network' },
  { name: 'RTS-GMLC', desc: 'NREL/DOE synthetic grid' },
];

// ---------------------------------------------------------------------------
// CreateAccountPage
// ---------------------------------------------------------------------------

export default function CreateAccountPage(): JSX.Element {
  const navigate = useNavigate();

  const handleStartFree = (): void => {
    navigate('/register');
  };

  const handleStartTrial = (): void => {
    navigate('/register');
  };

  const handleContactSales = (): void => {
    window.location.href = 'mailto:info@spenergy.solutions';
  };

  const handleChatWithUs = (): void => {
    openLiveChat();
  };

  return (
    <div data-testid="create-account-page" className={styles.page}>
      {/* ================================================================
          HERO SECTION
          ================================================================ */}
      <section className={styles.hero} aria-label="Page hero">
        {/* Animated background — grid art, low opacity */}
        <GridArtSVG />

        {/* Navigation bar */}
        <nav className={styles.heroNav} aria-label="Main navigation">
          <div className={styles.heroBrand}>
            <MangoGridIcon size={32} />
            <span className={styles.heroWordmark}>MangoGridStudio</span>
          </div>
          <button
            type="button"
            className={styles.heroSignIn}
            onClick={(): void => { navigate('/login'); }}
            aria-label="Sign in to your account"
          >
            Sign in
          </button>
        </nav>

        {/* Hero content */}
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            Power Grid Optimization Platform
          </h1>
          <p className={styles.heroSubtitle}>
            From IEEE 118-bus research to full production SCUC — MangoGridStudio gives
            power systems engineers the solver stack they need.
          </p>

          {/* Live-metrics bar */}
          <div className={styles.metricsBar} role="status" aria-label="Live grid metrics">
            <span className={styles.metricChip}>
              <span className={styles.metricDot} aria-hidden="true" />
              System Load: 142,450 MW
            </span>
            <span className={styles.metricChip}>
              <span className={styles.metricDot} aria-hidden="true" />
              Binding Constraints: 7
            </span>
            <span className={styles.metricChip}>
              <span className={styles.metricDot} aria-hidden="true" />
              Market Price: $38.20/MWh
            </span>
            <span className={styles.metricChip}>
              <span className={styles.metricDot} aria-hidden="true" />
              DA-UC solved in 2.3s
            </span>
          </div>
        </div>
      </section>

      {/* ================================================================
          "DESIGNED FOR" PERSONA SECTION
          ================================================================ */}
      <section className={styles.personaSection} aria-label="Who this platform is built for">
        <h2 className={styles.personaSectionTitle}>
          Built for every role in the control room
        </h2>
        <div className={styles.personaGrid}>

          {/* Grid Operators */}
          <div className={styles.personaTile}>
            <div className={styles.personaIconWrap} aria-hidden="true">
              <IconGridOperator />
            </div>
            <p className={styles.personaTitle}>Grid Operators</p>
            <p className={styles.personaDesc}>
              Real-time constraint monitoring, N-1 contingency alerts, and LMP
              tracking across your entire network topology.
            </p>
          </div>

          {/* Market Analysts */}
          <div className={styles.personaTile}>
            <div className={styles.personaIconWrap} aria-hidden="true">
              <IconMarketAnalyst />
            </div>
            <p className={styles.personaTitle}>Market Analysts</p>
            <p className={styles.personaDesc}>
              FTR opportunity detection, congestion pattern analysis, and LMP
              decomposition grounded in PJM Manual 11.
            </p>
          </div>

          {/* Power Systems Researchers */}
          <div className={styles.personaTile}>
            <div className={styles.personaIconWrap} aria-hidden="true">
              <IconResearcher />
            </div>
            <p className={styles.personaTitle}>Power Systems Researchers</p>
            <p className={styles.personaDesc}>
              IEEE 118-bus and RTS-GMLC test cases out of the box. Pluggable solver
              backends: HiGHS, Benders, ADMM, Lagrangian relaxation.
            </p>
          </div>

          {/* Utilities & ISOs */}
          <div className={styles.personaTile}>
            <div className={styles.personaIconWrap} aria-hidden="true">
              <IconUtility />
            </div>
            <p className={styles.personaTitle}>Utilities &amp; ISOs</p>
            <p className={styles.personaDesc}>
              Production-scale SCUC for PJM and MISO footprints. Multi-user
              operations workspace with on-premise deployment option.
            </p>
          </div>
        </div>
      </section>

      {/* ================================================================
          PLAN CARDS SECTION
          ================================================================ */}
      <section className={styles.plansSection} aria-label="Account plans">
        <h2 className={styles.plansSectionTitle}>Select your account type</h2>
        <p className={styles.plansSectionSubtitle}>
          All accounts include access to the MangoGridStudio platform.
        </p>

        <div className={styles.cardsRow} role="list">

          {/* --- Card 1: Free --- */}
          <article
            className={styles.card}
            role="listitem"
            aria-label="Public Account — Free plan"
          >
            <div className={`${styles.cardHeader} ${styles.cardHeaderFree}`}>
              <span className={styles.cardBadge}>Free</span>
            </div>
            <div className={styles.cardBody}>
              <h3 className={styles.cardTitle}>Public Account</h3>
              <p className={styles.cardDescription}>
                For researchers, students, and engineers exploring power systems
                optimization. Includes IEEE test systems and educational solver.
              </p>
              <ul className={styles.featureList} aria-label="Free plan features">
                <FeatureItem included text="IEEE 118-bus network viewer" />
                <FeatureItem included text="Educational SCUC solver (HiGHS)" />
                <FeatureItem included text="5 saved scenarios / case studies" />
                <FeatureItem included text="Community case library (IEEE test systems)" />
                <FeatureItem included text="N-1 contingency viewer" />
                <FeatureItem included={false} text="Real-time ISO LMP data feeds" />
                <FeatureItem included={false} text="Benders / ADMM advanced solvers" />
                <FeatureItem included={false} text="PTDF/LODF computation" />
                <FeatureItem included={false} text="API access" />
              </ul>
              <button
                type="button"
                className={`${styles.ctaButton} ${styles.ctaFree}`}
                onClick={handleStartFree}
                aria-label="Create a free public account"
              >
                Create Free Account
              </button>
            </div>
          </article>

          {/* --- Card 2: Free Trial (highlighted) --- */}
          <article
            className={`${styles.card} ${styles.cardTrial}`}
            role="listitem"
            aria-label="Full Platform Trial — most popular plan"
          >
            {/* "Most Popular" ribbon */}
            <span className={styles.mostPopularRibbon} aria-label="Most popular plan">
              Most Popular
            </span>

            <div className={`${styles.cardHeader} ${styles.cardHeaderTrial}`}>
              <span className={styles.cardBadge}>30-Day Trial</span>
            </div>
            <div className={styles.cardBody}>
              <h3 className={styles.cardTitle}>Full Platform Trial</h3>
              <p className={styles.cardDescription}>
                Evaluate the complete platform including real-time ISO data, advanced
                decomposition solvers, and AI-powered market analysis. No credit card
                required.
              </p>
              <ul className={styles.featureList} aria-label="Trial plan features">
                <FeatureItem included text="Everything in Free" />
                <FeatureItem included text="Real-time LMP feeds (PJM · MISO · CAISO)" />
                <FeatureItem included text="DA-UC with Benders decomposition" />
                <FeatureItem included text="ADMM zonal coordination" />
                <FeatureItem included text="PTDF/LODF matrix computation" />
                <FeatureItem included text="N-1 contingency screening" />
                <FeatureItem included text="FTR opportunity analysis" />
                <FeatureItem included text="AI Analyst (natural language queries)" />
                <FeatureItem included text="50 saved scenarios" />
                <FeatureItem included text="API access" />
              </ul>
              <button
                type="button"
                className={`${styles.ctaButton} ${styles.ctaTrial}`}
                onClick={handleStartTrial}
                aria-label="Start a 30-day free trial"
              >
                Start Free Trial
              </button>
              <p className={styles.ctaNote}>
                Trial access expires after 30 days. No credit card required.
              </p>
            </div>
          </article>

          {/* --- Card 3: Enterprise --- */}
          <article
            className={styles.card}
            role="listitem"
            aria-label="Enterprise Full License plan"
          >
            <div className={`${styles.cardHeader} ${styles.cardHeaderEnterprise}`}>
              <span className={styles.cardBadge}>Enterprise</span>
            </div>
            <div className={styles.cardBody}>
              <h3 className={styles.cardTitle}>Full License</h3>
              <p className={styles.cardDescription}>
                For utilities, ISOs, and consulting firms requiring production-grade
                infrastructure, SLAs, and dedicated power systems support.
              </p>
              <ul className={styles.featureList} aria-label="Enterprise plan features">
                <FeatureItem included text="Everything in Trial" />
                <FeatureItem included text="PJM/MISO production-scale SCUC" />
                <FeatureItem included text="Stochastic UC with Monte Carlo" />
                <FeatureItem included text="Custom ISO integrations (ERCOT · SPP · NYISO)" />
                <FeatureItem included text="Multi-user operations workspace" />
                <FeatureItem included text="On-premise deployment option" />
                <FeatureItem included text="99.9% SLA &amp; dedicated support" />
              </ul>
              <button
                type="button"
                className={`${styles.ctaButton} ${styles.ctaEnterprise}`}
                onClick={handleContactSales}
                aria-label="Contact sales for enterprise pricing"
              >
                Contact Sales
              </button>
              <p className={styles.ctaNote}>
                Pricing based on system scale and user count.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* ================================================================
          FEATURE COMPARISON TABLE
          ================================================================ */}
      <section className={styles.comparisonSection} aria-label="Feature comparison table">
        <div className={styles.comparisonInner}>
          <h2 className={styles.comparisonTitle}>Compare plans</h2>
          <table className={styles.comparisonTable} aria-label="Feature comparison across account plans">
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col">Free</th>
                <th scope="col">Trial</th>
                <th scope="col">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.feature}>
                  <td>{row.feature}</td>
                  <td><CellValue value={row.free} /></td>
                  <td><CellValue value={row.trial} /></td>
                  <td><CellValue value={row.enterprise} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ================================================================
          SOLVER & STANDARDS STACK SECTION
          ================================================================ */}
      <section className={styles.solverSection} aria-label="Solver and standards stack">
        <div className={styles.solverSectionInner}>
          <h2 className={styles.solverSectionTitle}>
            Built on open standards. Validated against real markets.
          </h2>
          <p className={styles.solverSectionSubtitle}>
            Every solver and test network used in MangoGridStudio is grounded in
            published standards and publicly validated reference data.
          </p>
          <div className={styles.solverBadgeRow} role="list">
            {SOLVER_BADGES.map((badge) => (
              <div
                key={badge.name}
                className={styles.solverBadge}
                role="listitem"
              >
                <span className={styles.solverBadgeName}>{badge.name}</span>
                <span className={styles.solverBadgeDesc}>{badge.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          CONTACT SECTION
          ================================================================ */}
      <section className={styles.contactSection} aria-label="Contact and support">
        <div className={styles.contactInner}>
          <h2 className={styles.contactTitle}>Planning a production deployment?</h2>
          <p className={styles.contactSubtitle}>
            Whether you are evaluating for PJM, MISO, or a custom footprint — our
            power systems team can help scope the right solution.
          </p>
          <div className={styles.contactButtons}>
            <button
              type="button"
              className={styles.contactBtnOutline}
              onClick={handleChatWithUs}
              aria-label="Open live chat support"
            >
              Chat with us
            </button>
            <button
              type="button"
              className={styles.contactBtnAmber}
              onClick={handleContactSales}
              aria-label="Contact sales via email"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </section>

      {/* ================================================================
          FOOTER
          ================================================================ */}
      <footer className={styles.footer} role="contentinfo">
        <span className={styles.footerLeft}>
          &copy; 2026 MangoGridStudio. All rights reserved.
        </span>
        <nav className={styles.footerRight} aria-label="Footer links">
          <a href="#privacy" className={styles.footerLink}>
            Privacy Policy
          </a>
          <a href="#terms" className={styles.footerLink}>
            Terms of Service
          </a>
          <a href="#docs" className={styles.footerLink}>
            Documentation
          </a>
        </nav>
      </footer>
    </div>
  );
}
